// "Who receives invoices" on the client My Invoices page.
//
// Clients used to email us to change this. Each list is one billing scope:
// a parent-company member edits the list every brand uses, a single-client
// member edits that client's list (contacts the parent sets for every brand
// show as locked). Saving replaces the list; the server emails everyone on
// the old and new list.
import React, { useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';

import { toast } from '@src/components/core/toaster';
import { listMyInvoiceRecipients, saveMyInvoiceRecipients } from '@src/api/invoiceRecipients';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ScopeSection({ list, showName, onSaved }) {
  const [rows, setRows] = useState(list.recipients);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => setRows(list.recipients), [list]);

  const original = list.recipients.map((r) => r.email.toLowerCase()).sort().join(',');
  const current = rows.map((r) => r.email.toLowerCase()).sort().join(',');
  const dirty = original !== current;

  const add = () => {
    const email = newEmail.trim();
    if (!EMAIL_RE.test(email)) {
      toast.error('Enter a valid email address.');
      return;
    }
    if (rows.some((r) => r.email.toLowerCase() === email.toLowerCase())) {
      toast.error('That person already receives invoices.');
      return;
    }
    setRows([...rows, { email, name: newName.trim(), locked: false }]);
    setNewEmail('');
    setNewName('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const resp = await saveMyInvoiceRecipients({
        scope: list.scope,
        id: list.id,
        recipients: rows.map(({ email, name }) => ({ email, name })),
      });
      toast.success('Invoice recipients updated. Everyone affected will get an email.');
      onSaved(resp?.data?.list);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update invoice recipients.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {showName && (
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {list.name}
        </Typography>
      )}
      {list.scope === 'parent' && list.covers.length > 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Applies to all {list.covers.length} brands: {list.covers.join(', ')}
        </Typography>
      )}
      <Stack spacing={1}>
        {rows.map((r) => (
          <Stack key={r.email} direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2">{r.name ? `${r.name} · ${r.email}` : r.email}</Typography>
              {r.locked && (
                <Tooltip title={list.locked_note || 'Set for every brand by your parent company.'}>
                  <Chip size="small" icon={<LockIcon />} label="All brands" variant="outlined" />
                </Tooltip>
              )}
            </Stack>
            {!r.locked && (
              <IconButton
                size="small"
                aria-label={`Stop sending invoices to ${r.email}`}
                disabled={saving || rows.length <= 1}
                onClick={() => setRows(rows.filter((x) => x.email !== r.email))}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            sx={{ flex: 2 }}
          />
          <TextField
            size="small"
            label="Name (optional)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" onClick={add} disabled={saving || !newEmail.trim()}>
            Add
          </Button>
        </Stack>
        {dirty && (
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={save} disabled={saving || !rows.length}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button onClick={() => setRows(list.recipients)} disabled={saving}>
              Cancel
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default function InvoiceRecipientsCard() {
  const [lists, setLists] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    listMyInvoiceRecipients()
      .then((resp) => setLists(resp?.data?.scopes || []))
      .catch(() => setError('Could not load invoice recipients.'));
  }, []);

  useEffect(load, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!lists?.length) return null;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardHeader
        title="Who receives invoices"
        subheader="Invoices, payment reminders and receipts go to these people. Removing someone here only stops their invoices."
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Stack spacing={3}>
          {lists.map((list) => (
            <ScopeSection
              key={`${list.scope}-${list.id}`}
              list={list}
              showName={lists.length > 1}
              onSaved={(updated) =>
                updated
                  ? setLists(lists.map((l) => (l.scope === updated.scope && l.id === updated.id ? updated : l)))
                  : load()
              }
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
